import React from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Progress, 
  Tag, 
  List, 
  Alert,
  Typography,
  Divider,
  Space,
  Statistic,
  Tabs,
  Button
} from 'antd';
import {
  WarningOutlined,
  BulbOutlined,
  FileTextOutlined,
  DownloadOutlined,
  TrophyOutlined,
  DiffOutlined,
  UserOutlined,
  ReadOutlined,
  BankOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { Essay, Correction, ErrorRecord, DimensionScores, EssayTypes } from '../types';
import { renderAnnotatedText, ErrorAnnotation } from '../utils/renderAnnotatedText';
import './CorrectionResultDisplay.css';
import '../styles/correction-common.css';
import { generateComments } from '../utils/generateComments';
import { getTopicFromEssay } from '../utils/topicHelper';

const { Text } = Typography;
const { TabPane } = Tabs;

interface CorrectionResultDisplayProps {
  correctionData: {
    correction: Correction;
    errors?: ErrorRecord[];
    essay?: Essay;
    dimension_scores?: DimensionScores;
    corrected_version?: any;  // 添加范文数据
  };
  // 显示控制
  showFullDetails?: boolean;  // 显示完整详情
  showScoreCard?: boolean;    // 显示评分卡片
  showErrorList?: boolean;    // 显示错误列表
  showTabs?: boolean;         // 显示标签页
  showActions?: boolean;      // 显示操作按钮
  showComments?: boolean;     // 显示评语
  
  // 标签页配置
  tabs?: {
    showOriginal?: boolean;   // 显示原文批注
    showErrors?: boolean;     // 显示错误分析
    showStats?: boolean;      // 显示数据分析
  };
  
  // 回调函数
  onExport?: () => void;      // 导出回调
  onNavigate?: (path: string) => void; // 导航回调
}

const CorrectionResultDisplay: React.FC<CorrectionResultDisplayProps> = ({ 
  correctionData, 
  showFullDetails = false,
  showScoreCard = true,
  showErrorList = true,
  showTabs = true,
  showActions = false,
  showComments = true,
  tabs = {
    showOriginal: true,
    showErrors: true,
    showStats: false
  },
  onExport,
  onNavigate
}) => {
  const { correction, errors = [], essay, dimension_scores, corrected_version } = correctionData;
  
  // 根据作文类型获取各维度满分
  const topic = essay ? getTopicFromEssay(essay) : null;
  const essayType = topic?.essay_type || EssayTypes.CONTINUATION;
  const isApplication = essayType === EssayTypes.APPLICATION;
  
  // 优先使用后端返回的dimension_scores中的max_score
  const maxScores = {
    total: isApplication ? 15 : 25,
    language: dimension_scores?.language?.max_score || (isApplication ? 5 : 7),
    content: dimension_scores?.content?.max_score || (isApplication ? 6 : 9),
    coherence: dimension_scores?.coherence?.max_score || (isApplication ? 4 : 5),
    creativity: dimension_scores?.creativity?.max_score || (isApplication ? 0 : 4)
  };
  
  // 调试输出
  console.log('CorrectionResultDisplay - correctionData:', correctionData);
  console.log('CorrectionResultDisplay - errors:', errors);
  console.log('CorrectionResultDisplay - essay:', essay);

  const getScoreColor = (score: number, total: number) => {
    const percentage = (score / total) * 100;
    if (percentage >= 90) return '#52c41a';
    if (percentage >= 80) return '#73d13d';
    if (percentage >= 70) return '#faad14';
    if (percentage >= 60) return '#fa8c16';
    return '#f5222d';
  };

  // 获取总分等级（根据作文类型）
  const getGradeText = (score: number): string => {
    const topic = essay ? getTopicFromEssay(essay) : null;
    const essayType = topic?.essay_type || EssayTypes.CONTINUATION;
    
    if (essayType === EssayTypes.APPLICATION) {
      // 应用文写作（满分15分）
      if (score >= 13) return '第五档（优秀）';
      if (score >= 10) return '第四档（良好）';
      if (score >= 7) return '第三档（及格）';
      if (score >= 4) return '第二档（较差）';
      return '第一档（差）';
    } else {
      // 读后续写（满分25分）
      if (score >= 22) return '第六档（优秀）';
      if (score >= 18) return '第五档（良好）';
      if (score >= 15) return '第四档（中等）';
      if (score >= 11) return '第三档（及格）';
      if (score >= 6) return '第二档（较差）';
      return '第一档（差）';
    }
  };

  // 获取单项等级
  // const getItemGrade = (score: number): string => {
  //   const percent = score / 5 * 100;
  //   if (percent >= 90) return '优秀';
  //   if (percent >= 70) return '良好';
  //   if (percent >= 60) return '及格';
  //   return '需改进';
  // };

  // 获取维度分数等级文字
  const getItemGradeText = (score: number, maxScore: number): string => {
    const percent = (score / maxScore) * 100;
    if (percent >= 90) return '优秀';
    if (percent >= 70) return '良好';
    if (percent >= 60) return '及格';
    return '需改进';
  };

  // 获取维度分数等级颜色
  const getItemGradeColor = (score: number, maxScore: number): string => {
    const percent = (score / maxScore) * 100;
    if (percent >= 90) return 'green';
    if (percent >= 70) return 'blue';
    if (percent >= 60) return 'orange';
    return 'red';
  };

  // 获取等级颜色（根据作文类型）
  const getGradeColor = (score: number): string => {
    const topic = essay ? getTopicFromEssay(essay) : null;
    const essayType = topic?.essay_type || EssayTypes.CONTINUATION;
    
    if (essayType === EssayTypes.APPLICATION) {
      // 应用文写作（满分15分）
      if (score >= 13) return 'green';
      if (score >= 10) return 'blue';
      if (score >= 7) return 'orange';
      if (score >= 4) return 'gold';
      return 'red';
    } else {
      // 读后续写（满分25分）
      if (score >= 22) return 'green';
      if (score >= 18) return 'blue';
      if (score >= 15) return 'cyan';
      if (score >= 11) return 'orange';
      if (score >= 6) return 'gold';
      return 'red';
    }
  };

  const getErrorTypeTag = (type: string) => {
    const typeMap: { [key: string]: { color: string; text: string } } = {
      grammar: { color: 'red', text: '语法' },
      spelling: { color: 'orange', text: '拼写' },
      vocabulary: { color: 'gold', text: '词汇' },
      structure: { color: 'blue', text: '结构' },
      punctuation: { color: 'cyan', text: '标点' }
    };
    const config = typeMap[type] || { color: 'default', text: type };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const renderOriginalText = () => {
    // 优先使用 corrected_text（用户编辑后的文本），如果为空则使用 original_text
    // 这与后端批改时使用的文本保持一致
    const text = (essay as any)?.corrected_text || (essay as any)?.original_text || '';
    const errorList = errors || [];

    console.log('Text for annotation (corrected_text first):', text);
    console.log('Errors to mark:', errorList);
    
    if (!text) return <Text type="secondary">暂无原文内容</Text>;
    
    // 转换错误格式以适配共享函数
    const annotations: ErrorAnnotation[] = errorList.map(error => ({
      error_text: error.error_text,
      suggestion: error.suggestion,
      error_type: error.error_type,
      severity: error.severity
    }));
    
    // 使用共享的批注渲染函数
    const html = renderAnnotatedText(text, annotations, {
      showSuggestion: true,
      showTooltip: true
    });
    
    return (
      <div 
        className="original-text-display"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  return (
    <div className="correction-result-display">
      {/* 学生信息卡片 */}
      {essay && (essay.student || essay.class) && (
        <Card 
          className="student-info-card"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 16]} className="student-info-grid">
            {essay.student && (
              <Col xs={24} sm={8}>
                <Statistic 
                  title="学生姓名" 
                  value={essay.student.name || '-'} 
                  prefix={<UserOutlined className="student-info-icon" />}
                />
              </Col>
            )}
            {essay.class && (
              <>
                <Col xs={24} sm={8}>
                  <Statistic 
                    title="年级" 
                    value={essay.class.grade ? (
                      essay.class.grade <= 9 ? `初${['一', '二', '三'][essay.class.grade - 7] || essay.class.grade}` :
                      essay.class.grade <= 12 ? `高${['一', '二', '三'][essay.class.grade - 10] || essay.class.grade}` :
                      `${essay.class.grade}年级`
                    ) : '-'}
                    prefix={<ReadOutlined className="student-info-icon" />}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic 
                    title="班级" 
                    value={essay.class.name || '-'}
                    prefix={<BankOutlined className="student-info-icon" />}
                  />
                </Col>
              </>
            )}
          </Row>
        </Card>
      )}
      
      {/* 评分详情 */}
      {showScoreCard && (
      <Card 
        title={
          <Space className="score-card-title" wrap>
            <BarChartOutlined />
            <span>评分详情 (满分{topic?.essay_type === EssayTypes.APPLICATION ? 15 : 25}分 - {topic?.essay_type === EssayTypes.APPLICATION ? '应用文写作' : '读后续写'})</span>
          </Space>
        }
        className="score-card"
      >
        <div style={{ marginBottom: 24 }}>
          <Row align="middle" gutter={[12, 8]} className="score-summary-row">
            <Col xs={24} sm={6}>
              <Text strong style={{ fontSize: '16px' }}>总体得分:</Text>
            </Col>
            <Col xs={24} sm={18}>
              <Space align="center" wrap className="score-summary-value">
                <Text strong style={{ fontSize: '24px', display: 'inline' }}>
                  {correction.total_score}/{topic?.essay_type === EssayTypes.APPLICATION ? 15 : 25}
                </Text>
                <Tag color={getGradeColor(correction.total_score)} style={{ fontSize: '14px', marginLeft: '8px' }}>
                  {getGradeText(correction.total_score)}
                </Tag>
              </Space>
            </Col>
          </Row>
        </div>
        
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* 语言评分 */}
          <Row align="middle" gutter={[16, 8]} className="score-dimension-row">
            <Col xs={24} sm={4}>
              <Text strong>语言 Language</Text>
            </Col>
            <Col xs={24} sm={10}>
              <Progress 
                percent={(correction.language_score/maxScores.language)*100} 
                strokeColor={getScoreColor(correction.language_score, maxScores.language)}
                showInfo={false}
              />
            </Col>
            <Col xs={12} sm={5}>
              <Text>{correction.language_score.toFixed(1)}/{maxScores.language} ({((correction.language_score/maxScores.language)*100).toFixed(0)}%)</Text>
            </Col>
            <Col xs={12} sm={5}>
              <Tag color={getItemGradeColor(correction.language_score, maxScores.language)}>
                {getItemGradeText(correction.language_score, maxScores.language)}
              </Tag>
            </Col>
          </Row>
          
          {/* 内容评分 */}
          <Row align="middle" gutter={[16, 8]} className="score-dimension-row">
            <Col xs={24} sm={4}>
              <Text strong>内容 Content</Text>
            </Col>
            <Col xs={24} sm={10}>
              <Progress 
                percent={(correction.content_score/maxScores.content)*100} 
                strokeColor={getScoreColor(correction.content_score, maxScores.content)}
                showInfo={false}
              />
            </Col>
            <Col xs={12} sm={5}>
              <Text>{correction.content_score.toFixed(1)}/{maxScores.content} ({((correction.content_score/maxScores.content)*100).toFixed(0)}%)</Text>
            </Col>
            <Col xs={12} sm={5}>
              <Tag color={getItemGradeColor(correction.content_score, maxScores.content)}>
                {getItemGradeText(correction.content_score, maxScores.content)}
              </Tag>
            </Col>
          </Row>
          
          {/* 连贯性评分 */}
          <Row align="middle" gutter={[16, 8]} className="score-dimension-row">
            <Col xs={24} sm={4}>
              <Text strong>连贯性 Coherence</Text>
            </Col>
            <Col xs={24} sm={10}>
              <Progress 
                percent={(correction.coherence_score/maxScores.coherence)*100} 
                strokeColor={getScoreColor(correction.coherence_score, maxScores.coherence)}
                showInfo={false}
              />
            </Col>
            <Col xs={12} sm={5}>
              <Text>{correction.coherence_score.toFixed(1)}/{maxScores.coherence} ({((correction.coherence_score/maxScores.coherence)*100).toFixed(0)}%)</Text>
            </Col>
            <Col xs={12} sm={5}>
              <Tag color={getItemGradeColor(correction.coherence_score, maxScores.coherence)}>
                {getItemGradeText(correction.coherence_score, maxScores.coherence)}
              </Tag>
            </Col>
          </Row>
          
          {/* 创意评分 - 仅读后续写显示 */}
          {!isApplication && (
            <Row align="middle" gutter={[16, 8]} className="score-dimension-row">
              <Col xs={24} sm={4}>
                <Text strong>创意 Creativity</Text>
              </Col>
              <Col xs={24} sm={10}>
                <Progress 
                  percent={(correction.creativity_score/maxScores.creativity)*100} 
                  strokeColor={getScoreColor(correction.creativity_score, maxScores.creativity)}
                  showInfo={false}
                />
              </Col>
              <Col xs={12} sm={5}>
                <Text>{correction.creativity_score.toFixed(1)}/{maxScores.creativity} ({((correction.creativity_score/maxScores.creativity)*100).toFixed(0)}%)</Text>
              </Col>
              <Col xs={12} sm={5}>
                <Tag color={getItemGradeColor(correction.creativity_score, maxScores.creativity)}>
                  {getItemGradeText(correction.creativity_score, maxScores.creativity)}
                </Tag>
              </Col>
            </Row>
          )}
        </Space>
        
        <Divider />
        <Text type="secondary">
          等级标准: 优秀(23+) | 良好(20-22) | 及格(15-19) | 不及格(&lt;15)
        </Text>
      </Card>
      )}

      {/* 评语 */}
      {showComments && (
      <Card 
        className="comments-card"
        title={
          <Space>
            <BulbOutlined />
            <span>评语</span>
          </Space>
        }
        style={{ marginTop: 16 }}
      >
        <div className="comments-content">
          {generateComments(correction, essay, dimension_scores)}
        </div>
      </Card>
      )}

      {/* 详细内容标签页 - 根据showFullDetails和showTabs决定是否显示 */}
      {showFullDetails && showTabs && (
        <Card className="details-card" style={{ marginTop: 16 }}>
          <Tabs defaultActiveKey="1">
            <TabPane 
              tab={
                <Space>
                  <FileTextOutlined />
                  <span>原文批注</span>
                </Space>
              } 
              key="1"
            >
              <div className="text-display-container">
                {renderOriginalText()}
                <Divider />
                <Text type="secondary">字数统计：{(essay as any)?.word_count || 0} 词</Text>
              </div>
            </TabPane>
            
            <TabPane 
              tab={
                <Space>
                  <WarningOutlined />
                  <span>错误分析 ({errors.length})</span>
                </Space>
              } 
              key="2"
            >
              <List
                dataSource={errors.slice(0, 10)}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={getErrorTypeTag(item.error_type)}
                      title={
                        <Space>
                          <Text delete style={{ color: '#ff4d4f' }}>
                            {item.error_text}
                          </Text>
                          <Text>→</Text>
                          <Text strong style={{ color: '#52c41a' }}>
                            {item.suggestion}
                          </Text>
                        </Space>
                      }
                      description={item.suggestion}
                    />
                  </List.Item>
                )}
              />
              {errors.length === 0 && (
                <Alert
                  message="太棒了！"
                  description="没有发现明显的错误，继续保持！"
                  type="success"
                  showIcon
                />
              )}
              {errors.length > 10 && (
                <Alert
                  message={`还有 ${errors.length - 10} 个错误未显示`}
                  type="warning"
                  style={{ marginTop: 16 }}
                />
              )}
            </TabPane>

            {/* 进阶范文标签页 */}
            {corrected_version?.full_text && (
              <TabPane
                tab={
                  <Space>
                    <TrophyOutlined />
                    <span>进阶范文</span>
                  </Space>
                }
                key="3"
              >
                <div style={{ marginBottom: 16 }}>
                  <Alert
                    message="进阶版范文"
                    description="基于您的原文优点和批改建议生成的高质量范文，达到优秀水平。绿色高亮部分为主要改进点。"
                    type="success"
                    showIcon
                    icon={<TrophyOutlined />}
                  />
                </div>

                {/* 范文内容 - 带改进标记 */}
                <Card
                  title="范文正文"
                  bordered={false}
                  style={{ marginBottom: 16 }}
                >
                  <div
                    style={{
                      fontSize: '16px',
                      lineHeight: '1.8'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        // 先处理文本，移除多余的空行
                        let processedText = corrected_version.full_text
                          .split('\n')
                          .map((line: string) => line.trim())
                          .filter((line: string, index: number, array: string[]) => {
                            // 保留非空行，或者前后都是非空行的空行（段落分隔）
                            return line || (array[index - 1] && array[index + 1]);
                          })
                          .join('<br/>');

                        // 在文本中标记改进部分
                        if (corrected_version.major_changes && corrected_version.major_changes.length > 0) {
                          corrected_version.major_changes.forEach((change: any, index: number) => {
                            if (change.revised) {
                              // 清理改进文本，移除换行符
                              const cleanRevised = change.revised.replace(/\n/g, ' ').trim();

                              // 转义特殊字符用于正则匹配
                              const escapedText = cleanRevised.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

                              // 在处理后的文本中查找并替换
                              processedText = processedText.replace(
                                new RegExp(escapedText, 'g'),
                                `<span style="
                                  border-bottom: 2px solid #52c41a;
                                  padding-bottom: 1px;
                                  font-weight: 500;
                                  cursor: help;
                                " title="${change.reason || '语言优化'}">
                                  ${cleanRevised}
                                </span>`
                              );
                            }
                          });
                        }

                        return processedText;
                      })()
                    }}
                  />

                  <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Space>
                      <Text type="secondary" style={{ fontSize: '14px' }}>
                        <span style={{
                          borderBottom: '2px solid #52c41a',
                          paddingBottom: '1px'
                        }}>绿色下划线</span> 表示主要改进部分
                      </Text>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          const element = document.createElement('a');
                          const file = new Blob([corrected_version.full_text], {type: 'text/plain'});
                          element.href = URL.createObjectURL(file);
                          element.download = '进阶范文.txt';
                          document.body.appendChild(element);
                          element.click();
                          document.body.removeChild(element);
                        }}
                      >
                        下载范文
                      </Button>
                    </Space>
                  </div>
                </Card>

                {/* 改进点说明 */}
                {corrected_version.major_changes && corrected_version.major_changes.length > 0 && (
                  <Card
                    title={
                      <Space>
                        <DiffOutlined />
                        <span>改进点对比</span>
                      </Space>
                    }
                    bordered={false}
                  >
                    <div style={{ padding: '0 16px' }}>
                      {corrected_version.major_changes.map((change: any, index: number) => (
                        <div key={index} style={{
                          marginBottom: 16,
                          lineHeight: '1.8'
                        }}>
                          <div style={{ marginBottom: 8 }}>
                            <Tag color="blue" style={{ marginRight: 8 }}>改进 {index + 1}</Tag>
                            <Text type="secondary">{change.reason}</Text>
                          </div>

                          <div style={{
                            marginLeft: 20,
                            fontSize: '15px'
                          }}>
                            <div style={{ marginBottom: 4 }}>
                              <Text type="secondary">原文：</Text>
                              <Text style={{ marginLeft: 8 }}>{change.original}</Text>
                            </div>
                            <div>
                              <Text type="secondary">改进：</Text>
                              <Text strong style={{ marginLeft: 8, color: '#52c41a' }}>
                                {change.revised}
                              </Text>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </TabPane>
            )}
          </Tabs>
        </Card>
      )}

      {/* 简要错误列表 - 不显示完整详情时展示 */}
      {!showFullDetails && errors.length > 0 && (
        <Card 
          title={
            <Space>
              <WarningOutlined />
              <span>主要错误 (前5个)</span>
            </Space>
          }
          style={{ marginTop: 16 }}
        >
          <List
            size="small"
            dataSource={errors.slice(0, 5)}
            renderItem={(item) => (
              <List.Item>
                {getErrorTypeTag(item.error_type)}
                <Space>
                  <Text delete style={{ color: '#ff4d4f' }}>
                    {item.error_text}
                  </Text>
                  <Text>→</Text>
                  <Text strong style={{ color: '#52c41a' }}>
                    {item.suggestion}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
          {errors.length > 5 && (
            <Alert
              message={`共发现 ${errors.length} 个错误`}
              type="info"
              style={{ marginTop: 8 }}
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default CorrectionResultDisplay;
